import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'

// Components
import LoadingSpinner from './components/LoadingSpinner'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import NotFound from './pages/NotFound'

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const TicketsPage = lazy(() => import('./pages/TicketsPage'))
const MobilePage = lazy(() => import('./pages/MobilePage'))
const TicketDetailPage = lazy(() => import('./pages/TicketDetailPage'))
const CreateTicketPage = lazy(() => import('./pages/CreateTicketPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const RoutingPage = lazy(() => import('./pages/RoutingPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const HelpPage = lazy(() => import('./pages/HelpPage'))

// Overhaul: mobile Cognito components
const MobileTicketDetailV2 = lazy(() => import('./pages/mobile/MobileTicketDetailV2'))
const MobileTicketList = lazy(() => import('./pages/mobile/MobileTicketList'))
import ResponsiveRoute from './components/ResponsiveRoute'

/** Shell that picks Layout (desktop) or minimal mobile wrapper */
const ResponsiveRouteShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ResponsiveRoute
    mobile={<div className="min-h-screen bg-slate-950 text-slate-50">{children}</div>}
    desktop={<Layout>{children}</Layout>}
  />
}

function App() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-slate-400">Loading TRC Tickets...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <Suspense fallback={
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }>
        <Routes>
          {/* Public */}
          <Route path="/login" element={
            <ErrorBoundary>
              {!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" replace />}
            </ErrorBoundary>
          } />
          <Route path="/callback" element={
            <ErrorBoundary>
              <AuthCallback />
            </ErrorBoundary>
          } />
          <Route path="/auth/callback" element={
            <ErrorBoundary>
              <AuthCallback />
            </ErrorBoundary>
          } />

          {/* Mobile — public, token-based auth */}
          <Route path="/m/:token" element={
            <ErrorBoundary>
              <Suspense fallback={<div className="min-h-screen bg-slate-950 flex items-center justify-center"><LoadingSpinner size="lg" /></div>}>
                <MobilePage />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* TRA links to /new — redirect to /tickets/create with query params */}
          <Route path="/new" element={<Navigate to={`/tickets/create${window.location.search}`} replace />} />

          {/* Protected */}
          <Route path="/*" element={
            <ProtectedRoute>
              <ResponsiveRouteShell>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={
                    <ErrorBoundary>
                      <ResponsiveRoute
                        mobile={<Navigate to="/tickets" replace />}
                        desktop={<DashboardPage />}
                      />
                    </ErrorBoundary>
                  } />
                  <Route path="/tickets" element={
                    <ErrorBoundary>
                      <ResponsiveRoute
                        mobile={<MobileTicketList />}
                        desktop={<TicketsPage />}
                      />
                    </ErrorBoundary>
                  } />
                  <Route path="/tickets/new" element={<Navigate to="/tickets/create" replace />} />
                  <Route path="/tickets/create" element={<ErrorBoundary><CreateTicketPage /></ErrorBoundary>} />
                  <Route path="/tickets/:ticketId" element={
                    <ResponsiveRoute
                      mobile={<ErrorBoundary><MobileTicketDetailV2 /></ErrorBoundary>}
                      desktop={<ErrorBoundary><TicketDetailPage /></ErrorBoundary>}
                    />
                  } />
                  <Route path="/users" element={<ErrorBoundary><ProtectedRoute requiredRole={['admin', 'super_admin']}><UsersPage /></ProtectedRoute></ErrorBoundary>} />
                  <Route path="/categories" element={<ErrorBoundary><ProtectedRoute requiredRole={['admin', 'super_admin']}><CategoriesPage /></ProtectedRoute></ErrorBoundary>} />
                  <Route path="/settings" element={<ErrorBoundary><ProtectedRoute requiredRole={['admin', 'super_admin']}><SettingsPage /></ProtectedRoute></ErrorBoundary>} />
                  <Route path="/routing" element={<ErrorBoundary><ProtectedRoute requiredRole={['admin', 'super_admin']}><RoutingPage /></ProtectedRoute></ErrorBoundary>} />
                  <Route path="/profile" element={<ErrorBoundary><ProfilePage /></ErrorBoundary>} />
                  <Route path="/help" element={<ErrorBoundary><HelpPage /></ErrorBoundary>} />
                  <Route path="/404" element={<ErrorBoundary><NotFound /></ErrorBoundary>} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </ResponsiveRouteShell>
            </ProtectedRoute>
          } />
        </Routes>
      </Suspense>
    </div>
  )
}

function AuthCallback() {
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const oauthError = params.get('error')

    if (oauthError) {
      setError(`Authentication failed: ${params.get('error_description') || oauthError}`)
      return
    }

    if (!code) {
      window.location.href = '/login'
      return
    }

    // Exchange authorization code for tokens (same pattern as TRA)
    const domain = import.meta.env.VITE_COGNITO_DOMAIN
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
    const redirect = `${window.location.origin}/callback`

    fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        redirect_uri: redirect,
        code,
      }).toString(),
    })
      .then(resp => {
        if (!resp.ok) throw new Error('Token exchange failed')
        return resp.json()
      })
      .then(tokens => {
        // Parse JWT payload for username
        const idPayload = JSON.parse(atob(tokens.id_token.split('.')[1]))
        const username = idPayload['cognito:username'] || idPayload.sub
        const prefix = `CognitoIdentityServiceProvider.${clientId}`

        // Store Microsoft tokens — DO NOT store in Cognito SDK format
        // (SDK's getSession() throws "string did not match expected pattern" on OAuth tokens)
        localStorage.setItem('trc_ms_id_token', tokens.id_token)
        localStorage.setItem('trc_ms_access_token', tokens.access_token)
        localStorage.setItem('trc_ms_refresh_token', tokens.refresh_token || '')
        localStorage.setItem('trc_ms_provider', 'microsoft')

        // Reload to let AuthContext pick up the tokens
        window.location.href = '/dashboard'
      })
      .catch(err => {
        console.error('OAuth callback error:', err)
        setError('Sign-in failed. Please try again.')
      })
  }, [])

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="bg-slate-900 border border-red-500/20 rounded-xl p-8 max-w-md text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <a href="/login" className="text-cyan-400 hover:text-cyan-300 underline">Back to login</a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-slate-400">Completing sign-in...</p>
      </div>
    </div>
  )
}

export default App
